import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {assertAndContinue} from 'Asserts';
import * as BenchlingUtil from 'BenchlingUtil';
import {ChildEntityAwareTagSchema} from 'registry/child-entities/ChildEntityConstants';
import * as RegistryUtil from 'registry/RegistryUtil';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {shouldShowWarehouseName} from 'schemas/SchemaWarehouseNameField';
import getTestProps from 'test/lib/cypress/getTestProps';
import {GRAY_DARK} from 'ui-library/Colors';
import {MakeFieldsOptional} from 'util/Types';

import {TagSchemaSummaryGraphQL} from '../../../graphql-fragments/schemas/tag-schemas/__generated/TagSchemaSummaryGraphQL';

function navigateToTagSchema(e: React.MouseEvent<HTMLAnchorElement>, props: ChildEntitySchemaRowProps): void {
  BenchlingUtil.navigate(
    e,
    RegistryUtil.getTagSchemaSettingsUrl(props.ownerUrlId, props.registryId, props.childSchema.id)
  );
}

export interface ChildEntitySchemaRowProps {
  registryId: string;
  ownerUrlId: string;
  childSchema: TagSchemaSummaryGraphQL;
  childSchemaLinkField: SchemaField;
}

function ChildEntitySchemaRow(props: ChildEntitySchemaRowProps): JSX.Element {
  return (
    <div className="simpleDivTable-row">
      <div className="simpleDivTable-cell">
        <div className="simpleDivTable-cell-contents">
          <a
            href="#"
            onClick={(e) => {
              navigateToTagSchema(e, props);
            }}
          >
            {props.childSchema.name}
          </a>
        </div>
      </div>
      {shouldShowWarehouseName() ? (
        <div className="simpleDivTable-cell">
          <div className="simpleDivTable-cell-contents">
            <span className={css(styles.notEditable)}>{props.childSchema.sqlIdentifier}</span>
          </div>
        </div>
      ) : null}
      <div className="simpleDivTable-cell">{props.childSchemaLinkField.displayName}</div>
      {shouldShowWarehouseName() ? (
        <div className="simpleDivTable-cell">
          <div className="simpleDivTable-cell-contents">
            <span className={css(styles.notEditable)}>{props.childSchemaLinkField.systemName}</span>
          </div>
        </div>
      ) : null}
    </div>
  );
}

interface ChildEntitySchemaTableProps {
  registryId: string;
  ownerUrlId: string;
  parentSchema: ChildEntityAwareTagSchema;
  tagSchemas: Array<TagSchemaSummaryGraphQL>;
}

function ChildEntitySchemaTable(props: ChildEntitySchemaTableProps): JSX.Element | null {
  const childEntityRows: Array<ChildEntitySchemaRowProps> =
    props.tagSchemas.length > 0
      ? props.parentSchema.childEntityLinkConfigs
          .map((linkConfig) => {
            const childSchema = props.tagSchemas.find(
              (schema) => schema.id === linkConfig.childEntitySchemaId
            );
            assertAndContinue(
              childSchema != null,
              'Expected child entity link config to correspond to a childSchemaSummary'
            );
            return {
              childSchema,
              childSchemaLinkField: linkConfig.childSchemaLinkField,
              ownerUrlId: props.ownerUrlId,
              registryId: props.registryId,
            };
          })
          .filter(
            (
              link: MakeFieldsOptional<ChildEntitySchemaRowProps, 'childSchema'>
            ): link is ChildEntitySchemaRowProps =>
              link.childSchema != null &&
              !(link.childSchema.archiveRecord || link.childSchemaLinkField.archiveRecord)
          )
      : [];

  // We assume data for the child schemas should be available at this point.
  return props.parentSchema.childEntityLinkConfigs.length > 0 && childEntityRows.length > 0 ? (
    <div
      className="simpleDivTable simpleDivTable--wide u-large-spacing-bottom"
      {...getTestProps({component: 'ChildEntitySchemaTable', element: 'root'})}
    >
      <div className="simpleDivTable-header">
        <div className="simpleDivTable-headerCell simpleDivTable-header--title">Child entities</div>
        {shouldShowWarehouseName() ? <div className="simpleDivTable-headerCell">System name</div> : null}
        <div className="simpleDivTable-headerCell">Field name</div>
        {shouldShowWarehouseName() ? (
          <div className="simpleDivTable-headerCell">Field system name</div>
        ) : null}
      </div>
      {childEntityRows.map((rowProps, key) => (
        <ChildEntitySchemaRow key={key} {...rowProps} />
      ))}
    </div>
  ) : null;
}

const styles = StyleSheet.create({notEditable: {color: GRAY_DARK}});

export default ChildEntitySchemaTable;
